import type { Column } from "@tanstack/react-table";
import { useMemo, type HTMLAttributes } from "react";
import { ArrowUpDownIcon, SortAsc, SortDesc } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";

interface DataTableColumnHeaderProps<TData, TValue> extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  const isSorted = column.getIsSorted();

  const icon = useMemo(() => {
    if (isSorted === "desc") {
      return <SortDesc className="ml-2 size-4" />;
    } else if (isSorted === "asc") {
      return <SortAsc className="ml-2 size-4" />;
    } else {
      return <ArrowUpDownIcon className="ml-2 size-4" />;
    }
  }, [isSorted]);

  const onClick = () => {
    const isDesc = isSorted === "desc";

    if (isSorted) {
      column.toggleSorting(!isDesc);
    } else {
      column.toggleSorting(false);
    }
  };

  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  return (
    <div className={cn("flex items-center gap-x-2", className)}>
      <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={onClick}>
        <span>{title}</span>
        {icon}
      </Button>
    </div>
  );
};
